/*
  Small popover panels opened from a task card's hover actions / ⋯ menu, so the common edits happen
  in place without opening the task: Due date, Assign people, Move to section (the keyboard / touch
  alternative to drag & drop).
  Wave 2: the same searchable pickers serve the task pane (Assignees · Project · Location · Priority ·
  Section) and the bulk bar — no more walls of check boxes.
*/
import { useMemo, useState } from 'react';
import { Check, Flag, Plus, Search } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { formatDate, toDateInputValue } from '@/lib/format';
import { avatarFill, dateInDays, daysUntilNextMonday, initials, NO_SECTION, PRIORITY_OPTIONS, priorityMeta, projectLabel } from './taskModel';

const panelTitle = 'px-3 pt-3 text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3';

function PanelSearch({ value, onChange, placeholder, label }) {
  return (
    <div className="px-2 pt-2">
      <div className="flex h-8 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 focus-within:border-[color:var(--brand-soft-border)] focus-within:shadow-focus">
        <Search className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
        <input
          // Radix focuses the first focusable element when the popover opens.
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={label}
          className="h-full min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-4"
        />
      </div>
    </div>
  );
}

const optionClasses = 'flex min-h-[34px] w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] text-ink outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover disabled:cursor-default disabled:opacity-60';

function AddRow({ query, label, onAdd }) {
  const [busy, setBusy] = useState(false);
  const value = query.trim();
  if (!value || !onAdd) return null;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onAdd(value);
        } finally {
          setBusy(false);
        }
      }}
      className={cn(optionClasses, 'font-semibold text-brand-ink')}
    >
      <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label} “{value}”</span>
    </button>
  );
}

/** Due date: quick picks + calendar + clear. `onPick('YYYY-MM-DD' | '')`. */
export function DueDatePanel({ value, onPick, title = 'Due date' }) {
  const current = toDateInputValue(value);
  const selected = useMemo(() => {
    if (!current) return undefined;
    const [year, month, day] = current.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [current]);
  const quickPicks = [
    { label: 'Today', value: dateInDays(0) },
    { label: 'Tomorrow', value: dateInDays(1) },
    { label: 'Next week', value: dateInDays(daysUntilNextMonday()) },
  ];
  return (
    <div data-testid="task-due-panel">
      <div className="flex items-center justify-between gap-2 pr-2">
        <div className={panelTitle}>{title}</div>
        {current ? (
          <button
            type="button"
            onClick={() => onPick('')}
            className="mt-2 rounded px-1.5 py-0.5 text-[12px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear date
          </button>
        ) : null}
      </div>
      <div className="grid grid-cols-3 gap-1 px-2 pt-2">
        {quickPicks.map((pick) => (
          <button
            key={pick.label}
            type="button"
            onClick={() => onPick(pick.value)}
            title={formatDate(pick.value, { weekday: true })}
            aria-label={`${pick.label}, ${formatDate(pick.value, { weekday: true })}`}
            className={cn(
              'h-[30px] whitespace-nowrap rounded-lg border border-line px-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              current === pick.value && 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink'
            )}
          >
            {pick.label}
          </button>
        ))}
      </div>
      <Calendar
        mode="single"
        selected={selected}
        defaultMonth={selected}
        onSelect={(date) => { if (date) onPick(toDateInputValue(date)); }}
        weekStartsOn={1}
        className="px-2 pb-1 pt-2"
        classNames={{ month: 'space-y-2' }}
      />
    </div>
  );
}

/**
 * Assign people: searchable multi-select; each click saves (the card shows the result right away).
 * `onAdd(name)` (optional) offers "Add “name” as a new person" when nobody matches.
 * `footer` (optional) sits under the list — the bulk bar collects several people and applies them there.
 */
export function AssignPanel({ members, selectedIds, onToggle, onAdd, title = 'Assign people', footer = null }) {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle
      ? members.filter((member) => `${member.name} ${member.role || ''}`.toLowerCase().includes(needle))
      : members;
    // Selected people first, then alphabetical (the API already sorts by name).
    return [...list].sort((a, b) => Number(selectedIds.includes(b.id)) - Number(selectedIds.includes(a.id)));
  }, [members, query, selectedIds]);

  return (
    <div data-testid="task-assign-panel">
      <div className={panelTitle}>{title}</div>
      <PanelSearch value={query} onChange={setQuery} placeholder="Search people…" label="Search people" />
      <div role="group" aria-label="People" className="max-h-[264px] overflow-y-auto p-1.5">
        {visible.length ? visible.slice(0, 200).map((member) => {
          const checked = selectedIds.includes(member.id);
          return (
            <button
              key={member.id}
              type="button"
              aria-pressed={checked}
              onClick={() => onToggle(member)}
              className={optionClasses}
              data-testid={`task-assign-option-${member.id}`}
            >
              <span
                aria-hidden="true"
                className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[9.5px] font-bold text-white"
                style={{ background: avatarFill(member.id || member.name) }}
              >
                {initials(member.name)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{member.name}</span>
                {member.role ? <span className="block truncate text-[11.5px] text-ink-3">{member.role}</span> : null}
              </span>
              <Check className={cn('h-4 w-4 shrink-0 text-brand-ink', !checked && 'invisible')} aria-hidden="true" />
            </button>
          );
        }) : (
          <p className="px-2.5 py-3 text-[12.5px] text-ink-3">No one matches “{query}”.</p>
        )}
        {!visible.length ? <AddRow query={query} label="Add" onAdd={onAdd} /> : null}
      </div>
      {footer}
    </div>
  );
}

/**
 * Projects: searchable; multi-select (pane) or pick one (`single`, the bulk bar).
 * `lockedIds`: picked and kept (a task made from a stage / invoice keeps its project). `hint`: a line under the
 * title; `disabled` greys the list out (with `hint` saying why).
 */
export function ProjectPanel({
  projects,
  selectedIds = [],
  onToggle,
  single = false,
  title = 'Project',
  lockedIds = [],
  hint = '',
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle ? projects.filter((project) => projectLabel(project).toLowerCase().includes(needle)) : projects;
    return [...list].sort((a, b) => (
      Number(selectedIds.includes(b.id)) - Number(selectedIds.includes(a.id))
      || projectLabel(a).localeCompare(projectLabel(b))
    ));
  }, [projects, query, selectedIds]);
  return (
    <div data-testid="task-project-panel">
      <div className={panelTitle}>{title}</div>
      {hint ? <p className="px-3 pt-1 text-[12px] leading-[1.4] text-ink-3" data-testid="task-project-panel-hint">{hint}</p> : null}
      <PanelSearch value={query} onChange={setQuery} placeholder="Search projects…" label="Search projects" />
      <div role="group" aria-label="Projects" className="max-h-[264px] overflow-y-auto p-1.5">
        {visible.length ? visible.slice(0, 200).map((project) => {
          const checked = selectedIds.includes(project.id);
          const locked = lockedIds.includes(project.id);
          return (
            <button
              key={project.id}
              type="button"
              aria-pressed={single ? undefined : checked}
              disabled={disabled || locked}
              onClick={() => onToggle(project)}
              className={optionClasses}
              title={locked ? 'The task came from this project and stays in it' : undefined}
              data-testid={`task-project-option-${project.id}`}
            >
              <span className="min-w-0 flex-1 whitespace-normal break-words">{projectLabel(project)}</span>
              {locked ? <span className="shrink-0 text-[11.5px] font-semibold text-ink-3">Origin</span> : null}
              <Check className={cn('h-4 w-4 shrink-0 text-brand-ink', !checked && 'invisible')} aria-hidden="true" />
            </button>
          );
        }) : (
          <p className="px-2.5 py-3 text-[12.5px] text-ink-3">No project matches “{query}”.</p>
        )}
      </div>
    </div>
  );
}

/** Locations: searchable multi-select; `onAdd(name)` adds a new location to the list. */
export function LocationPanel({ locations, selected = [], onToggle, onAdd }) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle ? locations.filter((location) => location.toLowerCase().includes(needle)) : locations;
  const exact = locations.some((location) => location.toLowerCase() === needle);
  return (
    <div data-testid="task-location-panel">
      <div className={panelTitle}>Location</div>
      <PanelSearch value={query} onChange={setQuery} placeholder="Search or add a location…" label="Search locations" />
      <div role="group" aria-label="Locations" className="max-h-[264px] overflow-y-auto p-1.5">
        {visible.map((location) => {
          const checked = selected.includes(location);
          return (
            <button
              key={location}
              type="button"
              aria-pressed={checked}
              onClick={() => onToggle(location)}
              className={optionClasses}
              data-testid={`task-location-option-${location}`}
            >
              <span className="min-w-0 flex-1 truncate">{location}</span>
              <Check className={cn('h-4 w-4 shrink-0 text-brand-ink', !checked && 'invisible')} aria-hidden="true" />
            </button>
          );
        })}
        {!exact ? <AddRow query={query} label="Add location" onAdd={onAdd} /> : null}
        {!visible.length && !onAdd ? <p className="px-2.5 py-3 text-[12.5px] text-ink-3">No location matches “{query}”.</p> : null}
      </div>
    </div>
  );
}

/** Priority: High / Medium / Low, each with its flag colour. */
export function PriorityPanel({ value, onPick }) {
  return (
    <div data-testid="task-priority-panel">
      <div className={panelTitle}>Priority</div>
      <div role="group" aria-label="Priority" className="p-1.5">
        {PRIORITY_OPTIONS.map((option) => {
          const meta = priorityMeta(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={value === option}
              onClick={() => onPick(option)}
              className={optionClasses}
              data-testid={`task-priority-option-${option}`}
            >
              <Flag className={cn('h-4 w-4 shrink-0', meta.textClass)} aria-hidden="true" />
              <span className={cn('min-w-0 flex-1 font-semibold', meta.textClass)}>{option}</span>
              <Check className={cn('h-4 w-4 shrink-0 text-brand-ink', value !== option && 'invisible')} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Move to section: the keyboard / touch alternative to dragging the card. */
export function MovePanel({ sections, current, onPick, onAdd, title = 'Move to section' }) {
  const [query, setQuery] = useState('');
  const options = useMemo(() => {
    const all = [...sections, NO_SECTION];
    const needle = query.trim().toLowerCase();
    return needle ? all.filter((section) => section.toLowerCase().includes(needle)) : all;
  }, [query, sections]);
  const exact = [...sections, NO_SECTION].some((section) => section.toLowerCase() === query.trim().toLowerCase());
  return (
    <div data-testid="task-move-panel">
      <div className={panelTitle}>{title}</div>
      <PanelSearch value={query} onChange={setQuery} placeholder="Search sections…" label="Search sections" />
      <div role="group" aria-label="Sections" className="max-h-[264px] overflow-y-auto p-1.5">
        {options.length ? options.map((section) => {
          const isCurrent = section === current;
          return (
            <button
              key={section}
              type="button"
              disabled={isCurrent}
              onClick={() => onPick(section)}
              className={cn(optionClasses, section === NO_SECTION && 'text-ink-2')}
              data-testid={`task-move-option-${section}`}
            >
              <span className="min-w-0 flex-1 truncate">{section}</span>
              {isCurrent ? <span className="text-[11.5px] font-semibold text-ink-3">Current</span> : null}
            </button>
          );
        }) : (
          !onAdd ? <p className="px-2.5 py-3 text-[12.5px] text-ink-3">No section matches “{query}”.</p> : null
        )}
        {!exact ? <AddRow query={query} label="New section" onAdd={onAdd} /> : null}
      </div>
    </div>
  );
}
